import type { BbIconName } from '../../components/BbIcon'
import type { WizardStep } from '../../components/BbStepper'

/* The five steps drawn in "prototypes/BatchBooks Import - Select Type".
   Step 1 used to be folded into the upload page; it is now its own route. */
export const IMPORT_STEPS: WizardStep[] = [
  { key: 'type', label: 'Select Type' },
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Mapping' },
  { key: 'review', label: 'Review' },
  { key: 'import', label: 'Import' },
]

/* The prototype's "Key List" grouping. EntityDef carries no grouping field —
   only name, label, isTransaction and groupByField — so the headline list types
   are named here and everything else falls through to "Other List".
   A new list entity added server-side therefore appears automatically, it just
   lands under Other.

   Bundle sits here with the other five because that is where the export
   prototype draws it, not because it is structurally different — it is an Item
   of one kind underneath. */
export const KEY_LISTS = ['Account', 'Item', 'Customer', 'Vendor', 'Bundle']

/* The Transactions tab's four category headings, likewise absent from the
   contract. Any transaction entity the API returns that is not named here
   falls through to a trailing "Other" group, so a new entity type shows up
   rather than silently vanishing.

   These are the entity *names*, which is why `Payment` appears under sales as
   "Receive Payment" and `Purchase` under purchases as "Expense" — the card
   labels come from the API, the grouping keys do not. */
export const TRANSACTION_GROUPS: Array<{ heading: string; names: string[] }> = [
  {
    heading: 'Customers & Sales',
    names: ['Invoice', 'Estimate', 'CreditMemo', 'SalesReceipt', 'Payment', 'RefundReceipt'],
  },
  {
    heading: 'Vendors & Purchases',
    names: [
      'PurchaseOrder',
      'Purchase',
      'Bill',
      'VendorCredit',
      'BillPayment',
      'CreditCardCredit',
      'Check',
    ],
  },
  { heading: 'Employees', names: ['TimeActivity'] },
  {
    heading: 'Banking & Others',
    names: ['Deposit', 'Transfer', 'JournalEntry', 'InventoryAdjustment', 'BankStatement', 'CreditCardStatement'],
  },
]

/* Per-entity glyphs, also absent from the contract. Anything unmapped gets a
   neutral icon rather than no icon. */
const ICONS: Record<string, BbIconName> = {
  Account: 'wallet',
  Item: 'box',
  Bundle: 'package',
  Customer: 'users',
  Vendor: 'store',
  Class: 'category',
  Employee: 'badge',
  Department: 'pin',
  Location: 'pin',
  Term: 'list',
  PaymentMethod: 'credit-card',
  Invoice: 'receipt',
  Estimate: 'edit-note',
  CreditMemo: 'receipt',
  SalesReceipt: 'receipt',
  Payment: 'credit-card',
  RefundReceipt: 'undo',
  PurchaseOrder: 'cart',
  Expense: 'wallet',
  Purchase: 'cart',
  Bill: 'receipt',
  VendorCredit: 'credit-card',
  BillPayment: 'credit-card',
  CreditCardCredit: 'credit-card',
  Check: 'list',
  TimeActivity: 'timer',
  Deposit: 'bank',
  Transfer: 'arrow-right',
  JournalEntry: 'book',
  InventoryAdjustment: 'package',
}

export function iconFor(entityName: string): BbIconName {
  return ICONS[entityName] ?? 'folder'
}
